/**
 * Plugin that asynchronously loads the Here maps scripts. The consuming component needs to call the load
 * method to get safe access to the API
 */

import config from '@/config/config.js';
import type { App, Plugin } from 'vue';

export const heremaps: Plugin = {
  install(app: App) {
    const majorVersion = config.here.majorVersion;
    const minorVersion = config.here.minorVersion;
    const url = `https://js.api.here.com/${majorVersion}/${minorVersion}`;
    app.provide('heremaps', {
      async load() {
        if (typeof window.H === 'undefined') {
          await loadJs(`${url}/mapsjs-core.js`);
          await loadJs(`${url}/mapsjs-service.js`);
          const promiseUi = loadJs(`${url}/mapsjs-ui.js`);
          const promiseEvents = loadJs(`${url}/mapsjs-mapevents.js`);
          const promiseCss = loadCss(`${url}/mapsjs-ui.css`);
          await Promise.all([promiseUi, promiseEvents, promiseCss]);
          return window.H;
        }
        return window.H;
      },
    });

    app.component('HereMap', () => import('@/components/HereMap.vue'));
  },
};

function loadJs(srcUrl: string) {
  return new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');

    script.src = srcUrl;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      resolve();
    };

    script.onerror = reject;
    script.onabort = reject;

    document.head.appendChild(script);
  });
}

function loadCss(srcUrl: string) {
  return new Promise<void>((resolve) => {
    const cssTag = document.createElement('link');
    cssTag.type = 'text/css';
    cssTag.rel = 'stylesheet';
    cssTag.href = srcUrl;
    cssTag.onload = () => {
      resolve();
    };
    document.head.append(cssTag);
  });
}
